import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { InlineLoader } from "@/components/common/Loader";
import { OPS } from "@/constants/testIds";
import { dkMessage, fmt, fmtQuantity, fmtSigned, fmtWeekday, isIsoMonth, monthsBack, unitOf } from "./dkUtils";
import { DayPanel } from "./DayPanel";
import { LocationPicker, MonthPicker, NoteBox, OfflineBox, cardClass, chipClass, dataStateText } from "./widgets";

const C = OPS.dk;
const M = OPS.dk.month;
// mode -> the cell field it shows; «الفرق» is the unexplained variance (after W&M), the flagged figure
const MODES = [
    ["used", "used", "dk_mode_used"],
    ["theoretical", "theoretical", "dk_mode_theoretical"],
    ["variance", "unexplained", "dk_mode_variance"],
];

const dayNum = (iso) => Number(String(iso).slice(8, 10));

/**
 * «الشهر»: one location, items down × days across, like the W&M monthly grid
 * (sticky first column; a blank cell is «·», never 0). The cells switch between
 * used / by sales / variance; flagged cells are red; the last column is the
 * month total, flagged by the same rule on sums. A cell opens the day detail.
 */
export function MonthGrid({ scope, canOpenDay }) {
    const { t, tn, lang } = useI18n();
    const [searchParams, setSearchParams] = useSearchParams();
    const locations = useMemo(() => scope?.locations || [], [scope]);
    const thisMonth = (scope?.today || "").slice(0, 7);
    const months = useMemo(() => monthsBack(thisMonth, 12), [thisMonth]);

    const paramLoc = searchParams.get("loc");
    const paramMonth = searchParams.get("month");
    const locationId = locations.some((l) => l.id === paramLoc) ? paramLoc
        : scope?.default_location_id || locations[0]?.id || "";
    const month = isIsoMonth(paramMonth) && months.includes(paramMonth) ? paramMonth : thisMonth;
    const location = locations.find((l) => l.id === locationId);
    // «حسب المبيعات» and «الفرق» are system figures: the operations manager only
    const modes = scope?.caps?.can_see_system ? MODES : MODES.filter(([k]) => k === "used");
    const [mode, setMode] = useState("used");
    const field = (modes.find((m) => m[0] === mode) || modes[0])[1];

    const setParam = (key, value) => {
        const next = new URLSearchParams(searchParams);
        next.set(key, value);
        setSearchParams(next, { replace: true });
    };

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [panelDate, setPanelDate] = useState(null);
    const todayRef = useRef(null);
    const seq = useRef(0);

    const load = useCallback(async () => {
        if (!locationId || !month) { setLoading(false); return; }
        const my = ++seq.current;
        setLoading(true);
        setError(null);
        try {
            const r = await api.get("/dk/month", { params: { location_id: locationId, month } });
            if (my === seq.current) setData(r.data);
        } catch (e) {
            if (my === seq.current) {
                setError(e);
                // a failed switch must not leave the old month's cells under the new label
                setData((d) => (d && (d.month !== month || d.location?.id !== locationId) ? null : d));
            }
        } finally {
            if (my === seq.current) setLoading(false);
        }
    }, [locationId, month]);
    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (todayRef.current) todayRef.current.scrollIntoView({ inline: "center", block: "nearest" });
    }, [data]);

    const days = data?.days || [];
    const rows = data?.rows || {};
    // an item the location does not count shows only where it still has stored lines (history)
    const items = (data?.items || []).filter((i) => !i.excluded || rows[i.id]);
    const states = data?.data_states || {};
    const today = scope?.today || "";
    const msg = error ? dkMessage(error, lang) : null;
    const coverage = data?.coverage || {};
    // a past day whose sales are missing (a future day is simply not there yet)
    const salesMissing = (d) => !!states[d] && states[d] !== "ok" && states[d] !== "no_sales" && (!today || d <= today);
    const notOkDays = days.filter(salesMissing).length;

    const cellText = (v) => (field === "unexplained" ? fmtSigned(v, lang) : fmtQuantity(v, lang));

    return (
        <div data-testid={M.view} className="space-y-3">
            <div className={`${cardClass} space-y-2 p-3`}>
                <div className="grid gap-2 sm:grid-cols-2">
                    <LocationPicker locations={locations} value={locationId} onChange={(v) => setParam("loc", v)} />
                    <MonthPicker months={months} value={month} onChange={(v) => setParam("month", v)} />
                </div>
                {modes.length > 1 ? (
                <div data-testid={M.mode} data-mode={mode} role="radiogroup" className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-0.5 scrollbar-thin">
                    {modes.map(([k, , label]) => (
                        <button key={k} type="button" role="radio" aria-checked={mode === k} data-testid={M.modeOption(k)}
                                data-active={mode === k ? "true" : "false"} onClick={() => setMode(k)} className={chipClass(mode === k)}>
                            {t(label)}
                        </button>
                    ))}
                </div>
                ) : null}
                {mode === "variance" ? <p className="text-[11px] text-[color:var(--ink-faint)]">{t("dk_mode_variance_hint")}</p> : null}
            </div>

            {error && !data ? (msg ? <NoteBox>{msg}</NoteBox> : <OfflineBox onRetry={load} busy={loading} />) : null}

            {loading && !data ? <InlineLoader /> : data ? (
                !items.length ? (
                    <p data-testid={M.empty}
                       className="rounded-[var(--radius-lg)] border border-dashed border-[color:var(--border-soft)] py-10 text-center text-sm text-[color:var(--ink-muted)]">
                        {t("dk_no_items")}
                    </p>
                ) : (
                    <div className={`overflow-x-auto rounded-[var(--radius-lg)] border border-[color:var(--border-soft)] bg-[color:var(--bg-surface)] transition-opacity duration-200 ${loading ? "opacity-60" : ""}`}>
                        <table data-testid={M.grid} data-mode={mode} className="w-max min-w-full border-collapse text-sm">
                            <thead>
                                <tr className="bg-[color:var(--bg-surface-2)]">
                                    <th className="sticky z-20 min-w-[7.5rem] max-w-[9rem] bg-[color:var(--bg-surface-2)] px-3 py-2 text-start text-xs font-bold text-[color:var(--ink-muted)]"
                                        style={{ insetInlineStart: 0 }}>
                                        {t("dk_item")}
                                    </th>
                                    {days.map((d) => {
                                        const st = states[d];
                                        const off = salesMissing(d);
                                        return (
                                            <th key={d} ref={d === today ? todayRef : null} title={off ? dataStateText(st, scope, lang, t) : fmtWeekday(d, lang)}
                                                data-state={st || ""}
                                                className={`px-2 py-2 text-center text-[11px] font-bold tabular-nums ${
                                                    d === today ? "bg-[color:var(--brand-orange-soft)] text-[color:var(--brand-brown)]" : "text-[color:var(--ink-faint)]"
                                                }`}>
                                                <bdi dir="ltr">{dayNum(d)}</bdi>
                                                {off ? (
                                                    <span aria-hidden className="mx-auto mt-0.5 block h-1.5 w-1.5 rounded-full bg-[color:var(--warning-soft-fg)]" />
                                                ) : null}
                                            </th>
                                        );
                                    })}
                                    <th className="px-3 py-2 text-center text-xs font-bold text-[color:var(--ink-muted)]">{t("dk_total")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((it) => {
                                    const row = rows[it.id] || {};
                                    const totals = row.totals || {};
                                    const unit = unitOf(it, t);
                                    return (
                                        <tr key={it.id} data-testid={M.row(it.id)} className="border-t border-[color:var(--separator)]">
                                            <td className="sticky z-10 bg-[color:var(--bg-surface)] px-3 py-2 text-start" style={{ insetInlineStart: 0 }}>
                                                <span className="block truncate text-xs font-semibold text-[color:var(--ink)]">{tn(it)}</span>
                                                <span className="text-[10px] text-[color:var(--ink-faint)]">{unit}</span>
                                            </td>
                                            {days.map((d) => {
                                                const cell = (row.days || {})[d] || {};
                                                const v = cell[field];
                                                const has = v !== null && v !== undefined;
                                                return (
                                                    <td key={d} data-testid={M.cell(it.id, d)} data-flag={cell.flag ? "true" : "false"}
                                                        data-value={has ? v : ""} data-state={cell.state || ""}
                                                        className={`px-1 py-1 text-center text-xs tabular-nums ${
                                                            cell.flag ? "bg-[color:var(--danger-soft)]" : d === today ? "bg-[color:var(--brand-orange-soft)]/40" : ""
                                                        }`}>
                                                        <button type="button" onClick={() => setPanelDate(d)}
                                                                aria-label={`${tn(it)} — ${fmtWeekday(d, lang)}`}
                                                                className={`min-h-8 min-w-[2.75rem] rounded px-1 font-semibold hover:underline ${
                                                                    cell.flag ? "text-[color:var(--danger-soft-fg)]" : has ? "text-[color:var(--ink)]" : "text-[color:var(--ink-faint)]"
                                                                }`}>
                                                            {has ? <bdi dir="ltr">{cellText(v)}</bdi> : "·"}
                                                        </button>
                                                    </td>
                                                );
                                            })}
                                            <td data-testid={M.total(it.id)} data-flag={totals.flag ? "true" : "false"} data-value={totals[field] ?? ""}
                                                className={`whitespace-nowrap px-3 py-2 text-center text-xs font-bold tabular-nums ${
                                                    totals.flag ? "bg-[color:var(--danger-soft)] text-[color:var(--danger-soft-fg)]" : "text-[color:var(--ink)]"
                                                }`}>
                                                {totals[field] === null || totals[field] === undefined ? "·" : <bdi dir="ltr">{cellText(totals[field])}</bdi>}
                                                <span className="block text-[10px] font-medium text-[color:var(--ink-faint)]">{unit}</span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )
            ) : null}

            {data ? (
                <div className="space-y-1 text-[11px] text-[color:var(--ink-faint)]">
                    {/* a counter never gets flags or sales states: its legend explains only «·» */}
                    <p>{t(scope?.caps?.can_see_system ? "dk_month_legend" : "dk_month_legend_counter")}</p>
                    {notOkDays ? <p>{fmt(t("dk_month_days_missing_sales"), { n: fmtQuantity(notOkDays, lang) })}</p> : null}
                    {coverage.unmatched_qty ? (
                        <p className="font-semibold text-[color:var(--warning-soft-fg)]">
                            {fmt(t("dk_coverage_unmatched"), { n: fmtQuantity(coverage.unmatched_qty, lang) })}
                        </p>
                    ) : null}
                </div>
            ) : null}

            <DayPanel locationId={panelDate ? locationId : null} locationName={location ? tn(location) : ""} date={panelDate}
                      scope={scope} canOpenDay={canOpenDay} onClose={() => setPanelDate(null)} />
        </div>
    );
}
